// src/components/gatherings/DeniedModal.tsx
import React, { useState } from 'react';
import { Modal, Box, IconButton, Typography, TextField, Button, CircularProgress, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GatheringStatus } from '../../../../types';
import { gatheringDataService } from '../../../../services/data/gatheringService';

interface DeniedModalProps {
  gatheringId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  token: string;
  gatheringTitle?: string;
}

// Style for the modal content using MUI patterns
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '600px',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: { xs: 2, sm: 4 },
  borderRadius: 2,
  outline: 0,
};

const DeniedModal: React.FC<DeniedModalProps> = ({
  gatheringId,
  isOpen,
  onClose,
  onSuccess,
  token,
  gatheringTitle
}) => {
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    // Validate notes
    if (!notes.trim()) {
      setError('Please provide a reason for denying this gathering request.');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Use the admin endpoint with notes
      await gatheringDataService.updateGatheringStatusAsAdmin(
        gatheringId,
        GatheringStatus.DENIED,
        notes
      );
      
      // Reset form and close modal
      setNotes('');
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      setError('Failed to update status. Please try again.');
      console.error('Error submitting notes:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setNotes('');
    setError(null);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={isSubmitting ? undefined : handleCancel}
      aria-labelledby="denied-modal-title"
    >
      <Box sx={modalStyle}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography id="denied-modal-title" variant="h6" component="h2">
            Deny Gathering Request
          </Typography>
          <IconButton 
            aria-label="close" 
            onClick={handleCancel}
            disabled={isSubmitting}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {gatheringTitle && (
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            <strong>Gathering:</strong> {gatheringTitle}
          </Typography>
        )}

        <Typography variant="body1" sx={{ mb: 2 }}>
          Please provide a reason for denying this gathering request.
          This information will be sent to the organizer via email.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          label="Reason for Denial"
          multiline
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Enter the reason for denying this gathering request..."
          fullWidth
          error={!!error}
          disabled={isSubmitting}
          sx={{ mb: 3 }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit} 
            disabled={isSubmitting || !notes.trim()}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : undefined}
            color="error"
          >
            {isSubmitting ? 'Submitting...' : 'Deny Gathering'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default DeniedModal;
